import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash2, ShoppingCart, ArrowDownToLine, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { inventoryService } from '@/services/inventory';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { InventoryItem, ItemUnit } from '@/types/inventory';

interface Props {
  item: InventoryItem | null;
}

/**
 * Per-item alternate UoMs. The base unit is `item.unitOfMeasure` (factor=1)
 * and is always shown at the top as a non-editable pinned row. Operators
 * add alternates with a multiplier (e.g. BOX = 12 PIECE) and tag one as
 * the default for purchases / sales so receipts and orders auto-pick the
 * common UoM.
 */
export function ItemUnitsSection({ item }: Props) {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const itemId = item?.id ?? null;

  const unitsQuery = useQuery({
    queryKey: ['inventory', 'units', itemId],
    queryFn: () => inventoryService.listUnits(itemId!),
    enabled: !!itemId,
  });

  const setDefault = useMutation({
    mutationFn: ({
      unitId,
      role,
    }: {
      unitId: string;
      role: 'purchase' | 'sales';
    }) =>
      inventoryService.updateUnit(itemId!, unitId, {
        isPurchaseDefault: role === 'purchase' ? true : undefined,
        isSalesDefault: role === 'sales' ? true : undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory', 'units', itemId] });
    },
  });

  const remove = useMutation({
    mutationFn: (unitId: string) => inventoryService.deleteUnit(itemId!, unitId),
    onSuccess: () => {
      toast.success('UoM removed');
      qc.invalidateQueries({ queryKey: ['inventory', 'units', itemId] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not delete UoM'),
  });

  if (!item) return null;

  const units = unitsQuery.data ?? [];
  const baseUnit = item.unitOfMeasure;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-muted-foreground">
          Base unit: <span className="font-mono text-foreground">{baseUnit}</span>
          <span className="mx-1.5">·</span>
          stock is always stored in base
        </div>
        <Button size="sm" variant="outline" onClick={() => setCreating(true)}>
          <Plus className="h-3 w-3" />
          Add UoM
        </Button>
      </div>

      {creating && (
        <CreateUnitForm
          itemId={item.id}
          baseUnit={baseUnit}
          onClose={() => setCreating(false)}
        />
      )}

      <div className="overflow-hidden rounded-md border border-border/70">
        <div className="grid grid-cols-12 gap-2 border-b border-border/60 bg-muted/40 px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
          <div className="col-span-3">Code</div>
          <div className="col-span-3">Name</div>
          <div className="col-span-3 text-right">1 unit equals</div>
          <div className="col-span-2 text-center">Default for</div>
          <div className="col-span-1" />
        </div>

        {/* Pinned base-unit row */}
        <div className="grid grid-cols-12 items-center gap-2 border-b border-border/60 bg-primary/[0.03] px-3 py-2 text-[12.5px]">
          <div className="col-span-3 font-mono text-foreground">{baseUnit}</div>
          <div className="col-span-3 text-muted-foreground">Base unit</div>
          <div className="col-span-3 text-right font-mono tabular-nums text-muted-foreground">
            1.000000 {baseUnit}
          </div>
          <div className="col-span-2 text-center">
            <Badge variant="muted" className="text-[10px]">always</Badge>
          </div>
          <div className="col-span-1" />
        </div>

        {unitsQuery.isLoading ? (
          <div className="px-3 py-4 text-center text-[12px] text-muted-foreground">
            Loading…
          </div>
        ) : units.length === 0 ? (
          <div className="px-3 py-4 text-center text-[12px] text-muted-foreground">
            No alternate UoMs yet. Add one to receive in cases / boxes / pallets.
          </div>
        ) : (
          units.map((u) => (
            <UnitRow
              key={u.id}
              unit={u}
              baseUnit={baseUnit}
              onDelete={() => remove.mutate(u.id)}
              onSetPurchaseDefault={() =>
                setDefault.mutate({ unitId: u.id, role: 'purchase' })
              }
              onSetSalesDefault={() =>
                setDefault.mutate({ unitId: u.id, role: 'sales' })
              }
            />
          ))
        )}
      </div>
    </div>
  );
}

function UnitRow({
  unit,
  baseUnit,
  onDelete,
  onSetPurchaseDefault,
  onSetSalesDefault,
}: {
  unit: ItemUnit;
  baseUnit: string;
  onDelete: () => void;
  onSetPurchaseDefault: () => void;
  onSetSalesDefault: () => void;
}) {
  return (
    <div className="grid grid-cols-12 items-center gap-2 border-b border-border/60 px-3 py-2 text-[12.5px] last:border-0">
      <div className="col-span-3 font-mono text-foreground">{unit.code}</div>
      <div className="col-span-3 truncate text-foreground">{unit.name}</div>
      <div className="col-span-3 text-right font-mono tabular-nums text-foreground">
        {fmt.number(unit.conversionFactor)} {baseUnit}
      </div>
      <div className="col-span-2 flex items-center justify-center gap-0.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className={cn(
                'h-6 w-6',
                unit.isPurchaseDefault
                  ? 'text-info'
                  : 'text-muted-foreground hover:text-foreground',
              )}
              onClick={onSetPurchaseDefault}
            >
              <ArrowDownToLine className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {unit.isPurchaseDefault ? 'Default for purchases' : 'Set as default for purchases'}
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className={cn(
                'h-6 w-6',
                unit.isSalesDefault
                  ? 'text-success'
                  : 'text-muted-foreground hover:text-foreground',
              )}
              onClick={onSetSalesDefault}
            >
              <ShoppingCart className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {unit.isSalesDefault ? 'Default for sales' : 'Set as default for sales'}
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="col-span-1 flex justify-end">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-muted-foreground hover:text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Delete UoM</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

function CreateUnitForm({
  itemId,
  baseUnit,
  onClose,
}: {
  itemId: string;
  baseUnit: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [factorRaw, setFactorRaw] = useState('');
  const [purchaseDefault, setPurchaseDefault] = useState(false);
  const [salesDefault, setSalesDefault] = useState(false);

  const factor = Number(factorRaw);
  const validFactor = factorRaw && factor > 0 && Number.isFinite(factor);
  const valid =
    code.trim().length > 0 &&
    name.trim().length > 0 &&
    validFactor &&
    code.trim().toUpperCase() !== baseUnit.toUpperCase();

  const create = useMutation({
    mutationFn: () =>
      inventoryService.createUnit(itemId, {
        code: code.trim().toUpperCase(),
        name: name.trim(),
        conversionFactor: factor,
        isPurchaseDefault: purchaseDefault,
        isSalesDefault: salesDefault,
      }),
    onSuccess: () => {
      toast.success('UoM added');
      qc.invalidateQueries({ queryKey: ['inventory', 'units', itemId] });
      onClose();
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not create UoM'),
  });

  return (
    <div className="rounded-md border border-border/70 bg-card p-3.5">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          New UoM
        </div>
        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onClose}>
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-3">
          <label className="text-[11px] text-muted-foreground" htmlFor="uomCode">
            Code
          </label>
          <Input
            id="uomCode"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="BOX"
            className="mt-1 h-8 font-mono text-[12.5px]"
            maxLength={16}
          />
        </div>
        <div className="col-span-5">
          <label className="text-[11px] text-muted-foreground" htmlFor="uomName">
            Name
          </label>
          <Input
            id="uomName"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Box of 12"
            className="mt-1 h-8 text-[12.5px]"
          />
        </div>
        <div className="col-span-4">
          <label className="text-[11px] text-muted-foreground" htmlFor="uomFactor">
            1 {code || 'unit'} ={' '}
            <span className="text-foreground">
              ? <span className="font-mono">{baseUnit}</span>
            </span>
          </label>
          <Input
            id="uomFactor"
            type="number"
            min="0"
            step="0.000001"
            value={factorRaw}
            onChange={(e) => setFactorRaw(e.target.value)}
            placeholder="12"
            className="mt-1 h-8 text-right font-mono text-[12.5px] tabular-nums"
          />
        </div>

        <div className="col-span-12 flex flex-wrap items-center gap-3 border-t border-border/60 pt-3 text-[12px]">
          <label className="inline-flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              className="h-3.5 w-3.5"
              checked={purchaseDefault}
              onChange={(e) => setPurchaseDefault(e.target.checked)}
            />
            <ArrowDownToLine className="h-3 w-3 text-info" />
            <span>Default for purchases</span>
          </label>
          <label className="inline-flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              className="h-3.5 w-3.5"
              checked={salesDefault}
              onChange={(e) => setSalesDefault(e.target.checked)}
            />
            <ShoppingCart className="h-3 w-3 text-success" />
            <span>Default for sales</span>
          </label>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button size="sm" onClick={() => create.mutate()} disabled={!valid || create.isPending}>
          {create.isPending ? 'Adding…' : 'Add UoM'}
        </Button>
      </div>
    </div>
  );
}
